import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import PayWall from "@/pages/PayWall";
import Dashboard from "@/pages/Dashboard";
import Workflow from "@/pages/Workflow";
import Cases from "@/pages/Cases";
import Compare from "@/pages/Compare";
import Library from "@/pages/Library";
import Admin from "@/pages/Admin";
import RPLWorkflow from "@/pages/RPLWorkflow";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Refund from "@/pages/Refund";
import Contact from "@/pages/Contact";
import AppShell from "@/components/AppShell";

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  return children;
}

function RequirePaid({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin" && user.access_status !== "active") return <Navigate to="/paywall" replace />;
  return children;
}

function RequireAdmin({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function ScrollTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollTop />
        <Toaster richColors position="top-right" />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/refund" element={<Refund />} />
          <Route path="/contact" element={<Contact />} />

          <Route element={<RequireAuth><AppShell /></RequireAuth>}>
            <Route path="/paywall" element={<PayWall />} />
            <Route path="/dashboard" element={<RequirePaid><Dashboard /></RequirePaid>} />
            <Route path="/workflow" element={<RequirePaid><Workflow /></RequirePaid>} />
            <Route path="/workflow/:caseId" element={<RequirePaid><Workflow /></RequirePaid>} />
            <Route path="/rpl" element={<RequirePaid><RPLWorkflow /></RequirePaid>} />
            <Route path="/rpl/:caseId" element={<RequirePaid><RPLWorkflow /></RequirePaid>} />
            <Route path="/cases" element={<RequirePaid><Cases /></RequirePaid>} />
            <Route path="/compare" element={<RequirePaid><Compare /></RequirePaid>} />
            <Route path="/library" element={<RequirePaid><Library /></RequirePaid>} />
            <Route path="/admin" element={<RequireAdmin><Admin /></RequireAdmin>} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
